import { useEffect, useState, useRef } from "react";
import { Box, Avatar, Typography, Tab, Tabs, IconButton } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import "./client-details.css";
import Details from "./details/details";
import Users from "./users/users";
import ClientDivisions from "./client-divisions/client-divisions";
import Breadcrumb from "../../../../components/breadcrumb/breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientDetailsRequest,
  getClientDetailsSuccess,
} from "../../../../redux/clients/slice";
import { uploadClientLogoRequest } from "../../../../utils/APICalls";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import ClientDocuments from "./documents/documents";
import ProcessesProcedures from "./processes-procedures/processes-procedures";
import Forms from "./forms/forms";
import ClientOrders from "./orders/orders";
import ClientInvoices from "./invoices/invoices";
import ClientResults from "./results/results";
import ContactSpectrum from "../../../../components/contact-spectrum/constact-spectrum";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return (
    <>
      {data?.label}
      {data?.showWarning && (
        <InfoOutlinedIcon className="tab-label-warning-icon" />
      )}
    </>
  );
}

export default function ClientDetails() {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [value, setValue] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const { loading } = useSelector((state: any) => state.clients);
  const { clientDetails } = useSelector((state: any) => state.clients);

  const actualClientId = clientId || state?.id;

  useEffect(() => {
    dispatch(getClientDetailsSuccess({ response: null }));
    dispatch(getClientDetailsRequest({ id: actualClientId }));
  }, [actualClientId, dispatch]);

  useEffect(() => {
    if (pathname.includes("/users") && !pathname.includes("/users/")) {
      setValue(2);
    } else if (pathname.includes("/users/")) {
      setValue(2);
    } else if (pathname.includes("/forms") && !pathname.includes("/forms/")) {
      setValue(6);
    } else if (pathname.includes("/forms/")) {
      setValue(6);
    } else if (pathname.includes("/orders") && !pathname.includes("/orders/")) {
      setValue(3);
    } else if (pathname.includes("/orders/")) {
      setValue(3);
    }
  }, [pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue !== 2) {
      const newPath = pathname.replace("/users", "");
      navigate(newPath, {
        replace: true,
        state: {
          breadcrumb: {
            names: {
              [clientDetails?.id]: clientDetails?.clientName,
            },
          },
        },
      });
    }
    if (newValue !== 6) {
      const newPath = pathname.replace("/forms", "");
      navigate(newPath, {
        replace: true,
        state: {
          breadcrumb: {
            names: {
              [clientDetails?.id]: clientDetails?.clientName,
            },
          },
        },
      });
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5000000) {
        setShowErrorNotification(true);
        setErrorMessage("File size exceeds 5MB limit.");
        return;
      }
      setShowLoading(true);
      setErrorMessage("");
      const response = await uploadClientLogoRequest(state?.id, file);
      setShowLoading(false);
      if (response?.status === 204) {
        dispatch(getClientDetailsRequest({ id: state?.id }));
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    }
  };

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Box className="user-info-layout">
        <Box className="avatar-container">
          {showLoading && <Loading />}
          <Avatar
            className="user-avatar"
            alt={clientDetails?.clientName}
            variant="circular"
            src={clientDetails?.logoUrl}
          />
          <IconButton
            className="avatar-edit-button"
            size="small"
            color="primary"
            aria-label="upload picture"
            component="label"
          >
            <input
              hidden
              accept="image/*"
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
            />
            <EditIcon />
          </IconButton>
        </Box>
        <Box className="margin-left" sx={{ mt: 1 }}>
          <Typography variant="h4">{clientDetails?.clientName}</Typography>
          {clientDetails?.mailingAddress && (
            <Box className="user-details-content user-location-content">
              <LocationOnOutlinedIcon className="user-location-icon" />
              <Typography className="user-location-txt">
                {clientDetails?.mailingAddress?.city},{" "}
                {clientDetails?.mailingAddress?.state}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* {true && (<Box className="update-warning-layout">
                <InfoOutlinedIcon className="update-warning-icon" />
                <Box>
                    <Typography className="update-warning-title">In Progress: New</Typography>
                    <Typography className="update-warning-txt">Update all required sections in order to work with work orders.</Typography>
                </Box>
            </Box>)} */}

      <Box className="tab-header">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="user details tabs"
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab
            className="tab-label"
            label={<TabLabel label="Details" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Client Divisions" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Users" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Orders" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Invoices" showWarning={false} />}
          />{" "}
           
          <Tab
            className="tab-label"
            label={<TabLabel label="Results" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Forms" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={<TabLabel label="Documents" showWarning={false} />}
          />
          <Tab
            className="tab-label"
            label={
              <TabLabel label="Processes & Procedures" showWarning={false} />
            }
          />
        </Tabs>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <Details data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <ClientDivisions data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Users data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <ClientOrders data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <ClientInvoices clientDetails={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={5}>
        <ClientResults clientDetails={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={6}>
        <Forms data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={7}>
        <ClientDocuments data={clientDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={8}>
        <ProcessesProcedures data={clientDetails} />
      </CustomTabPanel>

      {loading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </>
  );
}
